/* eslint-disable no-nested-ternary */
import { COUNTRY_CODES, WAREHOUSE_CREATE } from '@constants';
import { WarehouseFormValues } from '@forms/WarehouseForm/types';
import { WarehouseForm } from '@forms/WarehouseForm/WarehouseForm';
import { useAuthHttp } from '@hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';

function buildWarehousePayload(data: WarehouseFormValues, isDraft: boolean) {
  return {
    warehouse: {
      isDraft,
      name: data.name,
      description: data.description,
      address: data.address,
      postcode: data.postcode,
      businessContactEmail: data.businessContactEmail,
      city: data.city,
      country: data.country,
      location: data.location,
      temperatureControlTypes: data.temperatureControl?.value,
      temperatureMonitoringCapeabilities:
        data.temperatureMonitoring?.map((temp) => temp.value) ?? [],
      loadingDocks: data.loadingBays,
      isOffDock: data.facilities?.value === 'offDock',
      isQuay: data.facilities?.value === 'quay',
      hasOnsiteOffice: data.hasOnsiteOffice,
      onsiteOfficePhoneNumber: {
        countryCode: COUNTRY_CODES.find(
          (country) => country.country_code === data.siteOfficeCountryCode
        )?.country_code,
        countryNumberPrefix: COUNTRY_CODES.find(
          (country) => country.country_code === data.siteOfficeCountryCode
        )?.phone_code.toString(),
        number: data.siteOfficePhoneNumber,
      },
      maxStorageCapacity: data.maxStorageCapacity,
      maxStorageCapacityAvailable: data.maxFLOXCapacity,
      currentlyAvailableCapacity: data.remainingCapacity,
      minStorageCapacityThreshold: data.minCapacityRequirement,
      minStorageTimeThreshold: data.minTerm,
      storageTimeUnitOfMeasure: data.termsUnitOfMeasure?.value,
      storageCapacityUnitOfMeasure: data.capacityUnitOfMeasure?.value,
      currency: data.currency?.value,
      palletReceiptAndPutawayPrice: data.receiptAndPutaway,
      palletPickingAndDespatchPrice: data.palletPickingAndDispatch,
      palletLayerPickingAndDespatchPrice: data.layerPickingAndDispatch,
      palletStoragePricePerWeek: data.storage,
      casePickingAndDespatchPrice: data.casePickingAndDispatch,
      subsequentCasePickingAndDespatchPrice:
        data.subsequentCasePickingAndDispatch,
      itemPickingAndDespatchPrice: data.itemPickingAndDispatch,
      subsequentItemPickingAndDespatchPrice:
        data.subsequentItemPickingAndDispatch,
      containerHandlingPrice: data.containerHandling,
      squareFeetStoragePricePerWeek: data.storage,
      squareMetreStoragePricePerWeek: data.storage,
      demurragePricePerDay: data.demurrageCharges,
      palletReturnsPrice: data.palletReturns,
      itemReturnsPrice: data.itemReturns,
      caseReturnsPrice: data.caseReturns,
      provisionOfPalletPrice: data.provisionOfPallet,
      auxiliaryServicePricePerManHour: data.auxiliaryServices,
      additionalSurchargesFlatFee: data.surcharges,
      extraInfo: data.extraInfo || null,
      numberOfShifts: data.shifts,
      minHeadcount: data.minimumHeadcount,
      maxHeadcount: data.maximumHeadcount,
      standardBusinessHoursType: data.schedule?.value,
      hasFlexibleBusinessHours: data.flexibleHoursOnDemand,
      customBusinessHours:
        data.schedule?.value === 'Other'
          ? [
              {
                day: 'Monday',
                open: !data.mondayWorkingHours.working
                  ? '00:00:00'
                  : data.mondayWorkingHours.openingTime,
                close: !data.mondayWorkingHours.working
                  ? '00:00:00'
                  : data.mondayWorkingHours.closingTime,
              },
              {
                day: 'Tuesday',
                open: !data.tuesdayWorkingHours.working
                  ? '00:00:00'
                  : data.tuesdayWorkingHours.openingTime,
                close: !data.tuesdayWorkingHours.working
                  ? '00:00:00'
                  : data.tuesdayWorkingHours.closingTime,
              },
              {
                day: 'Wednesday',
                open: !data.wednesdayWorkingHours.working
                  ? '00:00:00'
                  : data.wednesdayWorkingHours.openingTime,
                close: !data.wednesdayWorkingHours.working
                  ? '00:00:00'
                  : data.wednesdayWorkingHours.closingTime,
              },
              {
                day: 'Thursday',
                open: !data.thursdayWorkingHours.working
                  ? '00:00:00'
                  : data.thursdayWorkingHours.openingTime,
                close: !data.thursdayWorkingHours.working
                  ? '00:00:00'
                  : data.thursdayWorkingHours.closingTime,
              },
              {
                day: 'Friday',
                open: !data.fridayWorkingHours.working
                  ? '00:00:00'
                  : data.fridayWorkingHours.openingTime,
                close: !data.fridayWorkingHours.working
                  ? '00:00:00'
                  : data.fridayWorkingHours.closingTime,
              },
              {
                day: 'Saturday',
                open: !data.saturdayWorkingHours.working
                  ? '00:00:00'
                  : data.saturdayWorkingHours.openingTime,
                close: !data.saturdayWorkingHours.working
                  ? '00:00:00'
                  : data.saturdayWorkingHours.closingTime,
              },
              {
                day: 'Sunday',
                open: !data.sundayWorkingHours.working
                  ? '00:00:00'
                  : data.sundayWorkingHours.openingTime,
                close: !data.sundayWorkingHours.working
                  ? '00:00:00'
                  : data.sundayWorkingHours.closingTime,
              },
            ]
          : [],
      vehicleLoadingTimeWindows: [
        {
          day: 'Monday',
          open: !data.mondayLoadingHours.operating
            ? '00:00:00'
            : data.mondayLoadingHours.openingTime,
          close: !data.mondayLoadingHours.operating
            ? '00:00:00'
            : data.mondayLoadingHours.closingTime,
        },
        {
          day: 'Tuesday',
          open: !data.tuesdayLoadingHours.operating
            ? '00:00:00'
            : data.tuesdayLoadingHours.openingTime,
          close: !data.tuesdayLoadingHours.operating
            ? '00:00:00'
            : data.tuesdayLoadingHours.closingTime,
        },
        {
          day: 'Wednesday',
          open: !data.wednesdayLoadingHours.operating
            ? '00:00:00'
            : data.wednesdayLoadingHours.openingTime,
          close: !data.wednesdayLoadingHours.operating
            ? '00:00:00'
            : data.wednesdayLoadingHours.closingTime,
        },
        {
          day: 'Thursday',
          open: !data.thursdayLoadingHours.operating
            ? '00:00:00'
            : data.thursdayLoadingHours.openingTime,
          close: !data.thursdayLoadingHours.operating
            ? '00:00:00'
            : data.thursdayLoadingHours.closingTime,
        },
        {
          day: 'Friday',
          open: !data.fridayLoadingHours.operating
            ? '00:00:00'
            : data.fridayLoadingHours.openingTime,
          close: !data.fridayLoadingHours.operating
            ? '00:00:00'
            : data.fridayLoadingHours.closingTime,
        },
        {
          day: 'Saturday',
          open: !data.saturdayLoadingHours.operating
            ? '00:00:00'
            : data.saturdayLoadingHours.openingTime,
          close: !data.saturdayLoadingHours.operating
            ? '00:00:00'
            : data.saturdayLoadingHours.closingTime,
        },
        {
          day: 'Sunday',
          open: !data.sundayLoadingHours.operating
            ? '00:00:00'
            : data.sundayLoadingHours.openingTime,
          close: !data.sundayLoadingHours.operating
            ? '00:00:00'
            : data.sundayLoadingHours.closingTime,
        },
      ],
      dispatchCutoffTime: data.cutOffTime,
      minEavesHeight: {
        quantity: data.minEavesHeight,
        unitOfMeasure: data.minEavesHeightUnitOfMeasure,
      },
      constructionYear: data.constructionYear,
      grossInternalArea: {
        quantity: data.grossInternalArea,
        unitOfMeasure: data.grossInternalAreaUnitOfMeasure?.value,
      },
      mezzanineArea: {
        quantity: data.mezzanineArea,
        unitOfMeasure: data.mezzanineAreaUnitOfMeasure,
      },
      isRackingAvailable: data.rackingAvailable,
      individualRackDepth: data.individualRackDepth,
      individualRackHeight: data.individualRackHeight,
      individualRackWidth: data.individualRackWidth,
      totalRackHeight: data.totalRackHeight,
      storageCapabilities:
        data.specialFacilities?.map((facility) => facility.value) ?? [],
      materialHandlingEquipment:
        data.materialHandlingEquipment?.map((equipment) => equipment.value) ??
        [],
      yardSpace: {
        quantity: data.yardSpace,
        unitOfMeasure: data.yardSpaceUnitOfMeasure?.value,
      },
      vehicleAccessHeight: {
        quantity: data.vehicleAccessHeight,
        unitOfMeasure: data.vehicleAccessHeightUnitOfMeasure,
      },
      leveledLoadingDocks: data.levelledLoadingDocks,
      doubleHeightLoadingDocks: data.doubleHeightDocks,
      carParkingSpaces: data.carParkingSpaces,
      lorryParkingSpaces: data.lorryParkingSpaces,
      sectorExperience:
        data.experienceSectors?.map((sector) => sector.value) ?? [],
      warehouseOperations:
        data.warehouseOperations?.map((operation) => operation.value) ?? [],
      fulfilmentServices:
        data.fulfilmentServices?.map((service) => service.value) ?? [],
      fleet: [
        {
          type: 'MultiAxleArticMoreThan26t',
          count: data.articsOver26t,
        },
        {
          type: 'ThreeAxleRigidLessThan26t',
          count: data.rigidsUnder26t,
        },
        { type: 'TwoAxleRigid18t', count: data.rigidsUnder18t },
        {
          type: 'TwoAxleRigidLessThan7AndHalf5t',
          count: data.rigidsUnder7_5t,
        },
        {
          type: 'LgvLessThan3AndHalft',
          count: data.ldvsUnder3_5t,
        },
        {
          type: 'VanLessThan1AndHalft',
          count: data.vansUnder1_5t,
        },
        { type: 'SpecialOrOther', count: data.specialOther },
      ],
      additionalServices:
        data.additionalServices?.map((service) => service.value) ?? [],
      safetyAndSecurity:
        data.safetyAndSecurity?.map((item) => item.value) ?? [],
      systemsAvailable: data.systems?.map((system) => system.value) ?? [],
      accreditationsAndMemberships: data.accreditations?.includes({
        label: 'None',
        value: 'none',
      })
        ? []
        : (data.accreditations?.map((accreditation) => accreditation.value) ??
          []),
      accreditationOther: data.accreditationOther || null,
    },
    images:
      data.warehouseImages?.map((image) => ({
        id: crypto.randomUUID(),
        title: image.name,
        image,
      })) ?? [],
  };
}

export function CreateWarehouse() {
  useDocumentTitle('FLOX - Create Warehouse');
  const navigate = useNavigate();
  const authHttpFormData = useAuthHttp('multipart/form-data');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Warehouses', href: '/warehouse-master', current: false },
    { name: 'Add New Warehouse', href: '/warehouse/create', current: true },
  ];

  const handleOnSubmit = async (data: WarehouseFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = buildWarehousePayload(data, false);
      await authHttpFormData.post(WAREHOUSE_CREATE, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/warehouse-master');
    } catch (error: any) {
      const message =
        (error?.response?.status === 413 &&
          'Your upload is too large. Please use a smaller image and try again.') ||
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Something went wrong. Please check your details and try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnSaveDraft = async (data: WarehouseFormValues) => {
    setIsSavingDraft(true);
    setSubmitError(null);
    try {
      const payload = buildWarehousePayload(data, true);
      await authHttpFormData.post(WAREHOUSE_CREATE, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/warehouse-master');
    } catch (error: any) {
      const message =
        (error?.response?.status === 413 &&
          'Your upload is too large. Please use a smaller image and try again.') ||
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Something went wrong. Please check your details and try again.';
      setSubmitError(message);
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <WarehouseForm
      onSubmit={handleOnSubmit}
      onSaveDraft={handleOnSaveDraft}
      isSubmitting={isSubmitting}
      isSavingDraft={isSavingDraft}
      pages={pages}
      header='New Warehouse'
      submitError={submitError}
      onDismissError={() => setSubmitError(null)}
    />
  );
}
